/**
 * Shadow tokens — web implementation, emitting CSS box-shadow strings.
 *
 * `elevation` is accepted for signature parity with the native module, where it
 * is the Android shadow control; the browser derives depth from the offsets.
 */
const hexToRgba = (hex: string, alpha: number): string => {
  const normalized = hex.replace('#', '');
  const expanded =
    normalized.length === 3
      ? normalized
          .split('')
          .map(char => char + char)
          .join('')
      : normalized;

  const channel = (start: number): number =>
    Number.parseInt(expanded.slice(start, start + 2), 16);

  const [r, g, b] = [channel(0), channel(2), channel(4)];
  if ([r, g, b].some(Number.isNaN)) {
    return `rgba(0, 0, 0, ${alpha})`;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const createShadow = (
  _elevation = 4,
  color = '#000',
  opacity = 0.1,
  radius = 8,
  offsetX = 0,
  offsetY = 2,
) => ({
  boxShadow: `${offsetX}px ${offsetY}px ${radius}px ${hexToRgba(color, opacity)}`,
});

const shadows = {
  none: {},
  xs: createShadow(1, '#000', 0.05, 2, 0, 1),
  sm: createShadow(2, '#000', 0.08, 4, 0, 2),
  md: createShadow(4, '#000', 0.1, 8, 0, 4),
  lg: createShadow(8, '#000', 0.12, 12, 0, 6),
  xl: createShadow(12, '#000', 0.15, 16, 0, 8),
  '2xl': createShadow(16, '#000', 0.2, 24, 0, 12),
  card: createShadow(3, '#000', 0.08, 6, 0, 2),
  modal: createShadow(24, '#000', 0.25, 32, 0, 16),
  button: createShadow(4, '#2563EB', 0.3, 8, 0, 4),
};

export {shadows, createShadow};
